import { useEffect, type KeyboardEvent, type PointerEvent } from 'react';
import { MIN_DRAWER_WIDTH } from './drawerWidth';

const KEY_STEP = 32;

interface DrawerResizeHandleProps {
  /** The drawer's current (already clamped) width in px. */
  width: number;
  maxWidth: number;
  /** Live width updates during a drag or key press; the drawer clamps them. */
  onResize: (width: number) => void;
  /** Called once a resize settles (pointer up / cancel, key press) so the width can be persisted. */
  onResizeEnd: (width: number) => void;
  onDraggingChange: (dragging: boolean) => void;
}

/** The drawer's left-edge splitter: drag with a pointer, or ArrowLeft / ArrowRight to widen / narrow. */
export function DrawerResizeHandle({
  width,
  maxWidth,
  onResize,
  onResizeEnd,
  onDraggingChange,
}: DrawerResizeHandleProps) {
  // The drawer is pinned to the right edge, so its width is the distance from the pointer to that edge.
  const widthAt = (e: PointerEvent<HTMLDivElement>) => window.innerWidth - e.clientX;

  // The handle unmounts mid-drag when the drawer switches to full view (e.g. the viewport turns narrow).
  useEffect(() => () => onDraggingChange(false), [onDraggingChange]);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    onDraggingChange(true);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) onResize(widthAt(e));
  };

  const endDrag = (e: PointerEvent<HTMLDivElement>, next: number) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    onDraggingChange(false);
    onResize(next);
    onResizeEnd(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const delta = e.key === 'ArrowLeft' ? KEY_STEP : e.key === 'ArrowRight' ? -KEY_STEP : 0;
    if (delta === 0) return;
    e.preventDefault();
    const next = Math.max(MIN_DRAWER_WIDTH, Math.min(width + delta, maxWidth));
    onResize(next);
    onResizeEnd(next);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize assistant"
      aria-valuenow={Math.round(width)}
      aria-valuemin={MIN_DRAWER_WIDTH}
      aria-valuemax={maxWidth}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(e) => endDrag(e, widthAt(e))}
      onPointerCancel={(e) => endDrag(e, width)}
      // Capture can end without pointerup / pointercancel; never leave the drawer stuck in a drag.
      onLostPointerCapture={() => onDraggingChange(false)}
      onKeyDown={onKeyDown}
      className="absolute top-0 -left-1 z-10 h-full w-2 cursor-col-resize touch-none hover:bg-blue-500/20 focus:outline-none focus-visible:bg-blue-500/30 active:bg-blue-500/30"
    />
  );
}
